import React from 'react'
import format from 'date-fns/format'
import addWeeks from 'date-fns/add_weeks'
import isAfter from 'date-fns/is_after'
import isBefore from 'date-fns/is_before'
import isEqual from 'date-fns/is_equal'
import * as d3 from 'd3'
import './avocadoPage.scss'
import SingleAvocado from '../../components/viz/SingleAvocado'
import VisualBox from '../../components/viz/VisualBox'
import avocadoData from '../../datasets/avocado-totalus'

/**
 * Avocado seedSize maximum should be max 1.7 and min 0.7
 * Height should be in the range of...
 * 200 - 400 for 2 per row
 * 100 - 300 for 6 per row
 *
 * For each VisualBox, we will use Line 1 to
 */
const width = 400
const margin = { top: 5, right: 5, bottom: 5, left: 5 }

class AvocadoPage extends React.Component {
  constructor(props) {
    super(props)
    const minMaxDate = d3.extent(avocadoData, d => new Date(d.Date))
    const minMaxPrice = d3.extent(avocadoData, d => parseFloat(d.AveragePrice))
    const minMaxVolume = d3.extent(avocadoData, d => parseFloat(d.TotalVolume))
    this.state = {
      // scaleTime is a function to translate Date to horizontal offset values for the time slider
      scaleTime: d3
        .scaleTime()
        .range([margin.left, width - margin.right])
        .domain(minMaxDate),
      organicData: [],
      organicDates: [],
      conventionalData: [],
      conventionalDates: [],
      // scalePrice is a function to translate price to avocado seed size
      scalePrice: d3
        .scaleLinear()
        .range([0.7, 1.7])
        .domain(minMaxPrice),
      scaleVolume: d3
        .scaleLog()
        .range([200, 400])
        .domain(minMaxVolume),
      minMaxDate: minMaxDate,
      currentDate: minMaxDate[0],
      paused: false,
      currentOrganicData: {},
      currentConventionalData: {}
    }
  }

  componentDidMount() {
    const organicData = avocadoData
      .filter(d => {
        return d['type'] === 'organic'
      })
      .sort((a, b) => new Date(a.Date) - new Date(b.Date))
    const conventionalData = avocadoData
      .filter(d => {
        return d['type'] === 'conventional'
      })
      .sort((a, b) => new Date(a.Date) - new Date(b.Date))
    const organicDates = organicData.map(d => d.Date)
    const conventionalDates = conventionalData.map(d => d.Date)
    this.setState({
      organicData,
      conventionalData,
      organicDates,
      conventionalDates
    })
    this.dataChangeGenerator()
  }

  /**
   * Makes changes to this.state to update the current date and therefore the data shown
   */
  dataChangeGenerator = () => {
    setInterval(() => {
      if (this.state.paused === false) {
        if (isAfter(addWeeks(this.state.currentDate, 1), this.state.minMaxDate[1]))
          this.setState({ currentDate: this.state.minMaxDate[0] })
        else {
          const newDate = addWeeks(this.state.currentDate, 1)
          const organicIndex = this.state.organicDates.findIndex(d => {
            return (
              isEqual(newDate, new Date(d)) ||
              (isAfter(newDate, new Date(d)) && isBefore(this.state.currentDate, new Date(d)))
            )
          })
          const currentOrganicData =
            organicIndex >= 0 ? this.state.organicData[organicIndex] : this.state.currentOrganicData
          const conventionalIndex = this.state.conventionalDates.findIndex(d => {
            return (
              isEqual(newDate, new Date(d)) ||
              (isAfter(newDate, new Date(d)) && isBefore(this.state.currentDate, new Date(d)))
            )
          })
          const currentConventionalData =
            conventionalIndex >= 0
              ? this.state.conventionalData[conventionalIndex]
              : this.state.currentConventionalData
          this.setState({
            currentDate: newDate,
            currentOrganicData,
            currentConventionalData,
            organicIndex,
            conventionalIndex
          })
        }
      }
    }, 700)
  }

  handlePauseClick = e => {
    e.preventDefault()
    this.setState(prevState => {
      return { paused: !prevState.paused }
    })
  }

  render() {
    const {
      scaleTime,
      currentDate,
      paused,
      currentConventionalData,
      currentOrganicData,
      scalePrice,
      scaleVolume
    } = this.state

    // console.log(
    //   'currentOrganicData.AveragePrice',
    //   scalePrice(currentOrganicData.AveragePrice),
    //   'currentOrganicData.TotalVolume',
    //   scaleVolume(currentOrganicData.TotalVolume),
    //   'currentConventionalData.AveragePrice',
    //   scalePrice(currentConventionalData.AveragePrice),
    //   'currentConventionalData.TotalVolume',
    //   scaleVolume(currentConventionalData.TotalVolume)
    // )

    return (
      <div className="avocado-section">
        <h2 className="title">Historical Avocado Prices &amp; Volumes in the US</h2>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <button onClick={this.handlePauseClick} className="pause-button">
            {paused ? 'Resume' : 'Pause'}
          </button>
          <svg height="20" width="300" viewBox={`0 0 ${300} 20`} className="time-scale">
            <path d="M 0,10 l 300,0" stroke="#7db208" strokeWidth="1" />
            <circle
              cx={scaleTime(currentDate)}
              cy="10"
              r="10"
              stroke="#ffffff"
              strokeWidth="2"
              fill="#704012"
              textAnchor="middle"
              style={{ cursor: 'pointer' }}
            />
            <g textAnchor="middle">
              <text
                fill="#555"
                x={scaleTime(currentDate)}
                y="40"
                fontSize="0.75em"
                fontWeight="700"
              >
                {format(currentDate, 'MMM D, YYYY')}
              </text>
            </g>
          </svg>
        </div>
        <div className="avocado-row">
          <VisualBox
            title="Organic"
            label1={`Avg. Price (USD): $${
              currentOrganicData.AveragePrice
                ? parseFloat(currentOrganicData.AveragePrice).toFixed(2)
                : '0.00'
            }`}
            label2={`Total Volume: ${
              currentOrganicData.TotalVolume
                ? Math.round(currentOrganicData.TotalVolume).toLocaleString()
                : '0'
            }`}
          >
            <SingleAvocado
              seedSize={scalePrice(currentOrganicData.AveragePrice)}
              height={scaleVolume(currentOrganicData.TotalVolume)}
            />
          </VisualBox>
          <VisualBox
            title="Conventional"
            label1={`Avg. Price (USD): $${
              currentConventionalData.AveragePrice
                ? parseFloat(currentConventionalData.AveragePrice).toFixed(2)
                : '0.00'
            }`}
            label2={`Total Volume: ${
              currentConventionalData.TotalVolume
                ? Math.round(currentConventionalData.TotalVolume).toLocaleString()
                : '0'
            }`}
          >
            <SingleAvocado
              seedSize={scalePrice(currentConventionalData.AveragePrice)}
              height={scaleVolume(currentConventionalData.TotalVolume)}
            />
          </VisualBox>
        </div>
        <div style={{ minWidth: '400px', textAlign: 'center' }}>
          <p>Avocado by Mark Caron from the Noun Project</p>
          <p>
            Dataset from{' '}
            <a href="https://www.kaggle.com/neuromusic/avocado-prices" alt="Kaggle">
              Kaggle
            </a>
          </p>
          <p>Built with: React, SVG, SCSS</p>
        </div>
      </div>
    )
  }
}

export default AvocadoPage

// Attribute the avocado SVG to:
// Avocado by Mark Caron from the Noun Project



// WEBPACK FOOTER //
// ./src/pages/viz/avocado.js